//dependencies
import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { faCircleCheck, faClone } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { toast } from "@performyard/py-components";

import { AdminContainer, AdminTitle } from "../../../../../../components/Admin";
import Breadcrumbs from "../../../../../../components/Breadcrumbs";
import Button from "../../../../../../components/Button";
// components
import { CopyToClipboard } from "../../../../../../components/CopyToClipboard";
import { Grid, GridCol } from "../../../../../../components/Grid";
import Input from "../../../../../../components/Inputs/InputText";
import RevealContent from "../../../../../../components/RevealContent";
import Spacer from "../../../../../../components/Spacer";
import Spinner from "../../../../../../components/Spinner/ComponentLoader";
import Text from "../../../../../../components/Text";
import { StaticToast } from "../../../../../../components/Toast";
import Tooltip from "../../../../../../components/Tooltip";

//hooks
import { useGetApiKeyById } from "../../../../../../api/hooks";
import useForm from "../../../../../../hooks/useForm";

//api
import { recycleApiKey, updateApiKey } from "../../../../../../api/services";

import css from "./ViewApiKey.module.scss";

const formSchema = z.object({
	name: z.string().min(1).max(100),
	api_key: z.string(),
	api_token_id: z.string(),
});

const CopyButton = (props: { copyValue: string }) => {
	const { copyValue } = props;
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		setCopied(true);
		setTimeout(() => {
			setCopied(false);
		}, 2000);
	};

	return (
		<div className={css.copyButtonContainer}>
			<Tooltip message="Copied" placement="top" openOnClick hasArrow={false} turnOffAfter={2000}>
				<CopyToClipboard text={copyValue} onCopy={handleCopy}>
					{copied ? (
						<FontAwesomeIcon icon={faCircleCheck} className={css.copySuccess} />
					) : (
						<FontAwesomeIcon icon={faClone} className={css.copyButton} />
					)}
				</CopyToClipboard>
			</Tooltip>
		</div>
	);
};

const ViewApiKey = () => {
	// get api id from url
	const { apiId } = useParams();

	if (!apiId) {
		return;
	}

	const { data: resp, refetch, isLoading } = useGetApiKeyById(apiId);

	const { state, setInitialState, updateForm, validate, resetForm, setNewDefaultValues } = useForm({
		schema: formSchema,
		disableReactWarning: true,
	});

	const [hasChanges, setHasChanges] = React.useState(false);

	const validation = useMemo(() => validate(), [state]);

	const hostname = window.location.hostname === "localhost" ? "localhost:3000" : `api.${window.location.hostname}`;
	const curlCommand = `curl --location --request GET "https://${hostname}/v1/auth/verify" \\
    --header "x-api-key: ${resp?.data?.api_key}" \\
    --header "x-api-id: ${resp?.data?.api_token_id}" `;

	const refetchAndUpdateForm = async () => {
		const newResp = await refetch();
		if (newResp?.data?.data) {
			setNewDefaultValues({
				name: newResp.data.data.name,
				api_key: newResp.data.data.api_key,
				api_token_id: newResp.data.data.api_token_id,
			});
		}
	};

	const recycleMutation = useMutation({
		mutationFn: recycleApiKey,
		onSuccess: async () => {
			await refetchAndUpdateForm();
		},
		onError: () => {
			toast.error("Unable to recycle API key");
		},
	});

	const editMutation = useMutation({
		mutationFn: updateApiKey,
		onSuccess: async () => {
			await refetchAndUpdateForm();
			setHasChanges(false);

			toast.success("API key updated");
		},
		onError: () => {
			toast.error("Unable to edit API key");
		},
	});

	const handleChange = (name, value) => {
		if (state[name] !== value) {
			setHasChanges(true);
		}

		updateForm({
			[name]: value,
		});
	};

	const handleRest = () => {
		setHasChanges(false);
		resetForm();
	};

	useEffect(() => {
		if (!resp) return;

		setInitialState({
			name: resp?.data?.name,
			api_key: resp?.data?.api_key,
			api_token_id: resp?.data?.api_token_id,
		});
	});

	if (isLoading) {
		return <Spinner size={30} />;
	}

	if (!resp?.data) {
		return null;
	}

	return (
		<>
			<Sentry.ErrorBoundary fallback={undefined}>
				<AdminContainer>
					<div className={css.main}>
						{/* Title andBreadCrumbs */}
						<Grid cols={1}>
							<GridCol span={1}>
								<AdminTitle includeSpacer={false}>Authentication</AdminTitle>
								<Spacer height={2} />
								<div>
									<Breadcrumbs
										pages={[
											{
												pageID: "apikey",
												label: "API Key",
												url: "/administration/authentication-settings/api",
											},
											{
												pageID: "apikey-edit",
												label: resp?.data?.name,
												url: `/administration/authentication-settings/api/${
													resp?.data?._id || ""
												}`,
											},
										]}
									/>
								</div>
								<Spacer height={4} />
							</GridCol>
						</Grid>

						{/* Content */}
						<Grid cols={2}>
							{/* Left Column */}
							<GridCol width={300}>
								{/* API Key Name */}
								<Input
									value={state.name}
									label="Name"
									onChange={(value) => handleChange("name", value)}
									maxLength={100}
									errorMessage={validation.errors.name?.message ?? undefined}
								/>

								<Spacer height={2} />
								{/* API Key Name Controls */}
								<Grid cols={2} gap={1}>
									{hasChanges && (
										<GridCol width={"88px"}>
											<Button isHollow={true} size="large" onClick={handleRest}>
												Discard
											</Button>
										</GridCol>
									)}

									<GridCol width={"128px"}>
										<Button
											color="green"
											size="large"
											disabled={!hasChanges || state.name === ""}
											onClick={() => {
												editMutation.mutate({
													_id: resp?.data._id,
													name: state.name,
													enabled: resp?.data.enabled,
												});
											}}
											isRequesting={editMutation.isPending}
										>
											Save Changes
										</Button>
									</GridCol>
								</Grid>
							</GridCol>
							{/* Gap */}
							<GridCol width="1fr" />
							{/* Right Column */}
							<GridCol width={456}>
								{/* API ID */}
								<div>
									<Grid cols={1} gap={1}>
										<GridCol width="16px">
											<CopyButton copyValue={resp?.data.api_token_id} />
										</GridCol>
										<GridCol width="1fr">
											<Text
												element="label"
												size="paragraph-3"
												weight="semi-bold"
												htmlFor="apiIDInput"
											>
												API I.D.
											</Text>
										</GridCol>
									</Grid>

									<Spacer height={1} />

									<RevealContent label="Reveal API I.D.">
										<Input inputId="apiIDInput" value={resp?.data.api_token_id} disabled={true} />
									</RevealContent>
								</div>

								<Spacer height={2} />

								{/* API Key */}
								<div>
									<Grid cols={1} gap={1}>
										<GridCol width="16px">
											<CopyButton copyValue={resp?.data.api_key} />
										</GridCol>
										<GridCol width="1fr">
											<Text
												element="label"
												size="paragraph-3"
												weight="semi-bold"
												htmlFor="apiKeyInput"
											>
												API Key
											</Text>
										</GridCol>
									</Grid>

									<Spacer height={1} />

									<RevealContent label="Reveal API Key">
										<Input inputId="apiKeyInput" value={resp?.data.api_key} disabled={true} />
									</RevealContent>
								</div>

								<Spacer height={2} />

								{/* Sample Request */}
								<div>
									<Grid cols={1} gap={1}>
										<GridCol width="16px">
											<CopyButton copyValue={curlCommand} />
										</GridCol>
										<GridCol width="1fr">
											<Text size="paragraph-3" weight="semi-bold">
												Sample
											</Text>
										</GridCol>
									</Grid>
									<Spacer height={1} />
									<RevealContent label="Reveal Sample Request">
										<pre className={css.codeBlock}>
											<Text size="paragraph-3" weight="regular">
												{curlCommand}
											</Text>
										</pre>
									</RevealContent>
								</div>

								<Spacer height={2} />

								{/* Recycle Button */}
								<div>
									<Button
										size="large"
										color="blue"
										onClick={() => {
											recycleMutation.mutate({
												_id: resp?.data._id,
												name: resp?.data.name,
												enabled: resp?.data.enabled,
											});
										}}
										isRequesting={recycleMutation.isPending}
									>
										Recycle API Key
									</Button>
									<Spacer height={2} />
									<div>
										{recycleMutation.isSuccess && (
											<StaticToast
												type="success"
												message={"A New API Key has been Created"}
												size={"small"}
											/>
										)}

										{recycleMutation.isError && (
											<StaticToast type="error" message={"An Error Occurred"} size={"small"} />
										)}
									</div>
								</div>
							</GridCol>
						</Grid>
					</div>
				</AdminContainer>
			</Sentry.ErrorBoundary>
		</>
	);
};

export default ViewApiKey;
